import { useState, useRef, useEffect, useLayoutEffect } from 'react';

const TOOLTIP_MAX_WIDTH = 280;
const VIEWPORT_MARGIN = 8;

const KpiTooltip = ({ text, isMobile = false }: { text: string; isMobile?: boolean }) => {
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState({ top: 0, left: 0, caretLeft: 0 });
    const iconRef = useRef<SVGSVGElement>(null);
    const tooltipRef = useRef<HTMLDivElement>(null);

    const measure = () => {
        if (!iconRef.current) return;
        const r = iconRef.current.getBoundingClientRect();
        const iconCenterX = r.left + r.width / 2;
        const idealLeft = iconCenterX - TOOLTIP_MAX_WIDTH / 2;
        const clampedLeft = Math.max(
            VIEWPORT_MARGIN,
            Math.min(idealLeft, window.innerWidth - TOOLTIP_MAX_WIDTH - VIEWPORT_MARGIN),
        );
        const caretLeft = iconCenterX - clampedLeft;
        setPos({ top: r.top - 8, left: clampedLeft, caretLeft });
    };

    // After tooltip renders, reposition using actual rendered width so short
    // content doesn't leave unused space and the caret stays centred on the icon.
    useLayoutEffect(() => {
        if (!open || !tooltipRef.current || !iconRef.current) return;
        const actualWidth = tooltipRef.current.getBoundingClientRect().width;
        const iconRect = iconRef.current.getBoundingClientRect();
        const iconCenterX = iconRect.left + iconRect.width / 2;
        const idealLeft = iconCenterX - actualWidth / 2;
        const clampedLeft = Math.max(
            VIEWPORT_MARGIN,
            Math.min(idealLeft, window.innerWidth - actualWidth - VIEWPORT_MARGIN),
        );
        setPos({ top: iconRect.top - 8, left: clampedLeft, caretLeft: iconCenterX - clampedLeft });
    }, [open]);

    const handleMouseEnter = () => { measure(); setOpen(true); };
    const handleMobileClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!open) measure();
        setOpen(v => !v);
    };

    useEffect(() => {
        if (!isMobile || !open) return;
        const close = () => setOpen(false);
        window.addEventListener('scroll', close, { passive: true, capture: true });
        return () => window.removeEventListener('scroll', close, { capture: true });
    }, [isMobile, open]);

    return (
        <span
            style={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
            onMouseEnter={!isMobile ? handleMouseEnter : undefined}
            onMouseLeave={!isMobile ? () => setOpen(false) : undefined}
            onClick={isMobile ? handleMobileClick : undefined}
        >
            <svg
                ref={iconRef}
                width="10" height="10" viewBox="0 0 16 16" fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ cursor: isMobile ? 'pointer' : 'default', display: 'block' }}
            >
                <circle cx="8" cy="8" r="7" stroke="var(--t3, #9CA3B8)" strokeWidth="1.5" />
                <path d="M8 7.5v4" stroke="var(--t3, #9CA3B8)" strokeWidth="1.5" strokeLinecap="round" />
                <circle cx="8" cy="5.5" r="0.9" fill="var(--t3, #9CA3B8)" />
            </svg>
            {open && (
                <div
                    ref={tooltipRef}
                    style={{
                        position: 'fixed',
                        top: pos.top,
                        left: pos.left,
                        transform: 'translateY(-100%)',
                        width: 'max-content',
                        maxWidth: TOOLTIP_MAX_WIDTH,
                        background: 'var(--surface, #fff)',
                        border: '1px solid var(--card-border, #E2E8F0)',
                        borderRadius: 8,
                        padding: '8px 10px',
                        fontSize: 11,
                        color: 'var(--t2, #6B7280)',
                        lineHeight: 1.5,
                        whiteSpace: 'normal',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.14)',
                        zIndex: 9999,
                        pointerEvents: 'none',
                        textTransform: 'none',
                        fontWeight: 400,
                        letterSpacing: 0,
                    }}
                >
                    {text}
                    <div style={{
                        position: 'absolute',
                        bottom: -5,
                        left: pos.caretLeft,
                        transform: 'translateX(-50%) rotate(45deg)',
                        width: 8,
                        height: 8,
                        background: 'var(--surface, #fff)',
                        borderRight: '1px solid var(--card-border, #E2E8F0)',
                        borderBottom: '1px solid var(--card-border, #E2E8F0)',
                    }} />
                </div>
            )}
        </span>
    );
};

export default KpiTooltip;
